import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc } from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase';
import OrderManagement from './OrderManagement';
import ProductManagement from './ProductManagement';
import ProfileManagement from './ProfileManagement';
import AdminBusinessProfile from './AdminBusinessProfile';
import './AdminApp.css';

function AdminApp({ user, showOrders = true, showProducts = true }) {
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('orders');
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [ordersSnapshot, productsSnapshot] = await Promise.all([
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'products'))
      ]);

      const ordersData = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const productsData = productsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setOrders(ordersData.sort((a, b) => {
        const aTime = a.createdAt?.toMillis() || 0;
        const bTime = b.createdAt?.toMillis() || 0;
        return bTime - aTime;
      }));
      
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/admin/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: newStatus
      });
      await loadData();
    } catch (error) {
      console.error('Error updating order:', error);
    }
  };

  const addProduct = async (productData) => {
    try {
      await addDoc(collection(db, 'products'), productData);
      await loadData();
    } catch (error) {
      console.error('Error adding product:', error);
      throw error;
    }
  };

  const updateProduct = async (productId, productData) => {
    try {
      await updateDoc(doc(db, 'products', productId), productData);
      await loadData();
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  };

  const deleteProduct = async (productId) => {
    try {
      await deleteDoc(doc(db, 'products', productId));
      await loadData();
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  };

  return (
    <div className="admin-app">
      <div className="admin-tabs">
        <button 
          className={`admin-tab ${activeTab === 'orders' ? 'active' : ''}`}
          onClick={() => setActiveTab('orders')}
        >
          <span className="nav-emoji">📦</span>
          Orders
        </button>
        <button 
          className={`admin-tab ${activeTab === 'products' ? 'active' : ''}`}
          onClick={() => setActiveTab('products')}
        >
          <span className="nav-emoji">👕</span>
          Products
        </button>
        <button 
          className={`admin-tab ${activeTab === 'business' ? 'active' : ''}`}
          onClick={() => setActiveTab('business')}
        >
          <span className="nav-emoji">🏢</span>
          Business Profile
        </button>
        <button 
          className={`admin-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          <span className="nav-emoji">⚙️</span>
          Admin Profile
        </button>
      </div>

      <div className="admin-content">
        {loading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Loading...</p>
          </div>
        ) : (
          <>
            {activeTab === 'orders' && showOrders && (
              <OrderManagement
                orders={orders}
                onUpdateOrder={updateOrderStatus}
              />
            )}
            {activeTab === 'products' && showProducts && (
              <ProductManagement
                products={products}
                onAdd={addProduct}
                onUpdate={updateProduct}
                onDelete={deleteProduct}
              />
            )}
            {activeTab === 'business' && (
              <AdminBusinessProfile user={user} />
            )}
            {activeTab === 'profile' && (
              <ProfileManagement user={user} isAdmin={true} />
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default AdminApp;
